import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { resolveFieldDefaults } from '../core/operators';
import type { Filter, FilterConfig } from '../core/types';
import { AddFilterPopover } from './AddFilterPopover';
import { FilterChip } from './FilterChip';
import { FilterValue } from './FilterValue';

interface FilterBarProps<TWhere> {
    config: FilterConfig<TWhere>;
    filters: Filter[];
    whereClause?: TWhere;
    onAddFilter: (filterId: string) => string | undefined;
    onUpdateFilter: (id: string, updates: Partial<Filter>) => void;
    onRemoveFilter: (id: string) => void;
    onClearFilters: () => void;
}

/**
 * FilterBar Component
 *
 * Main UI component for the filter system. Displays active filters as compact chips,
 * with click-to-expand popover functionality for editing. Uses PostHog-inspired patterns.
 *
 * @component
 * @template TWhere - The Prisma where input type
 *
 * @example
 * ```typescript
 * function UserList() {
 *   const filterState = useFilterBuilder(filterConfig);
 *
 *   return (
 *     <FilterBar
 *       config={filterConfig}
 *       filters={filterState.filters}
 *       onAddFilter={filterState.addFilter}
 *       onUpdateFilter={filterState.updateFilter}
 *       onRemoveFilter={filterState.removeFilter}
 *       onClearFilters={filterState.clearFilters}
 *     />
 *   );
 * }
 * ```
 *
 * @features
 * - Compact chip display for applied filters
 * - Click chip to open popover for editing (doesn't push content down)
 * - Keyboard shortcut: Cmd+K (Mac) or Ctrl+K (PC) to add filter
 * - "Clear all" appears (inline, left) when 2+ removable filter chips are displayed
 *   (the pinned search box / pinned fields do not count toward the threshold)
 * - Pinned fields always visible
 * - Text search fields render as dedicated input
 */
export function FilterBar<TWhere>({
    config,
    filters,
    whereClause,
    onAddFilter,
    onUpdateFilter,
    onRemoveFilter,
    onClearFilters,
}: FilterBarProps<TWhere>) {
    // Track which filter is expanded for editing (null = none expanded)
    const [expandedFilterId, setExpandedFilterId] = useState<string | null>(null);

    // Create placeholder filters for pinned fields that don't have active filters
    const pinnedFilters = useMemo(() => {
        return config.filters
            .filter((f) => f.pinned === true)
            .map((fieldConfig) => {
                // Reuse the active filter if one exists, else build a placeholder.
                const activeFilter = filters.find((f) => f.filterId === fieldConfig.id);
                if (activeFilter) return activeFilter;

                const { operator, value } = resolveFieldDefaults(fieldConfig, { preferContains: true });
                return {
                    id: `pinned-placeholder-${fieldConfig.id}`,
                    filterId: fieldConfig.id,
                    operator,
                    value,
                } as Filter;
            });
    }, [config.filters, filters]);

    // Get regular (non-pinned) filters
    const regularFilters = filters.filter((filter) => !config.filters.find((fc) => fc.id === filter.filterId)?.pinned);

    // Handle adding a new filter and auto-expanding it
    const handleAddFilter = useCallback(
        (filterId: string) => {
            const newFilterId = onAddFilter(filterId);
            if (newFilterId) {
                // Auto-expand the newly added filter
                setExpandedFilterId(newFilterId);
            }
            return newFilterId;
        },
        [onAddFilter],
    );

    // Handle pinned filter updates
    const handlePinnedFilterUpdate = useCallback(
        (filter: Filter, updates: Partial<Filter>) => {
            const isPlaceholder = filter.id.startsWith('pinned-placeholder-');
            if (isPlaceholder) {
                // For placeholder filters, we need to add them first
                const newFilterId = onAddFilter(filter.filterId);
                if (newFilterId) {
                    onUpdateFilter(newFilterId, updates);
                }
            } else {
                onUpdateFilter(filter.id, updates);
            }
        },
        [onAddFilter, onUpdateFilter],
    );

    // Handle chip toggle expand
    const handleToggleExpand = useCallback((filterId: string) => {
        setExpandedFilterId((current) => (current === filterId ? null : filterId));
    }, []);

    return (
        <div className="flex w-full max-w-full flex-wrap items-center gap-2">
            {/* Pinned filters - always visible */}
            {pinnedFilters.map((filter) => {
                const fieldConfig = config.filters.find((fc) => fc.id === filter.filterId);
                const isTextSearch = fieldConfig?.type === 'textSearch';

                // For textSearch fields, render just the input.
                // Keyed by field id (not filter instance id): the first keystroke converts the
                // pinned placeholder into a real filter with a fresh id, and an id-based key would
                // remount the input mid-typing and drop focus.
                if (isTextSearch && fieldConfig) {
                    return (
                        <div key={filter.filterId} className="inline-block min-w-[200px] max-w-[400px]">
                            <FilterValue
                                value={filter.value}
                                operator={filter.operator}
                                fieldConfig={fieldConfig}
                                whereClause={whereClause}
                                onChange={(value) => handlePinnedFilterUpdate(filter, { value })}
                            />
                        </div>
                    );
                }

                // For non-textSearch pinned fields, render as chip (non-removable)
                // Note: Pinned fields don't use the chip/builder pattern - they stay inline
                return (
                    <div
                        key={filter.filterId}
                        className="inline-flex items-center gap-1.5 rounded-full border border-accent-200/80 bg-card px-3 py-1 text-sm"
                    >
                        <span className="font-medium text-muted-foreground">{fieldConfig?.label}</span>
                        <FilterValue
                            value={filter.value}
                            operator={filter.operator}
                            fieldConfig={fieldConfig!}
                            whereClause={whereClause}
                            onChange={(value) => handlePinnedFilterUpdate(filter, { value })}
                        />
                    </div>
                );
            })}

            {/* Regular filters as chips with popover */}
            {regularFilters.map((filter) => (
                <FilterChip
                    key={filter.id}
                    filter={filter}
                    config={config}
                    whereClause={whereClause}
                    isExpanded={expandedFilterId === filter.id}
                    onToggleExpand={() => handleToggleExpand(filter.id)}
                    onUpdate={(updates) => onUpdateFilter(filter.id, updates)}
                    onRemove={() => onRemoveFilter(filter.id)}
                />
            ))}

            {/* Clear all — left-aligned, in the flow; only shown when 2+ removable chips are displayed
                (pinned search box / pinned fields are not counted). */}
            {regularFilters.length > 1 && (
                <Tooltip title="Clear all filters">
                    <Button type="tertiary" danger onClick={onClearFilters} className="text-xs">
                        Clear all
                    </Button>
                </Tooltip>
            )}

            {/* Add filter — inline so it sits next to the search / after the chips */}
            <AddFilterPopover config={config} filters={filters} onAddFilter={handleAddFilter} />

            {/* No filters message */}
            {filters.length === 0 && (
                <span className="text-muted-foreground text-xs md:text-sm">No filters applied</span>
            )}
        </div>
    );
}
